import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import "../styles/ContactModel.css";

const ContactModel = ({ show, setShow }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    company_name: "",
    type: "",
    source: "Quick Contact Form",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 10) value = value.slice(0, 10);
    setFormData({ ...formData, phone_number: value });
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    if (id === "phone_number") return;
    setFormData({ ...formData, [id]: value });
  };

  const toUrlEncoded = (obj) =>
    Object.keys(obj)
      .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]))
      .join("&");

  const submitForm = async (payload, retryCount = 0) => {
    const maxRetries = 1; // Only retry once
    const endpoints = [
      '/api/popup-form.php', // Try relative path first
      'https://www.flexistore.in/api/popup-form.php' // Fallback to full URL
    ];

    try {
      const response = await axios({
        method: 'post',
        url: endpoints[retryCount % endpoints.length],
        data: toUrlEncoded(payload),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        withCredentials: true
      });
      return response;
    } catch (error) {
      if (retryCount < maxRetries) {
        return submitForm(payload, retryCount + 1);
      }
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("");

    // Client-side validation
    if (formData.full_name.length < 2 || formData.full_name.length > 50) {
      setSubmitStatus("Full name must be between 2 and 50 characters.");
      setIsSubmitting(false);
      return;
    }

    if (formData.phone_number.length !== 10) {
      setSubmitStatus("Phone number must be exactly 10 digits.");
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        utm_source: localStorage.getItem("utm_source"),
        utm_medium: localStorage.getItem("utm_medium"),
        utm_campaign: localStorage.getItem("utm_campaign"),
        utm_term: localStorage.getItem("utm_term"),
        page_title: document.title,
        page_url: window.location.href,
      };

      // Try to submit the form
      await submitForm(payload);

      // If successful, proceed with navigation
      setShow(false);
      navigate("/thank-you", { state: { formData } });
    } catch (error) {
      console.error('Form submission error:', error);
      
      // More specific error messages
      let errorMessage = "Something went wrong. Please try again.";
      
      if (error.response) {
        // Server responded with an error status
        if (error.response.status === 403) {
          errorMessage = "Access denied. Please try again or contact support.";
        } else if (error.response.status >= 500) {
          errorMessage = "Server error. Please try again later.";
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = "Network error. Please check your connection and try again.";
      }
      
      setSubmitStatus(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setShow(false);

    // Reset form after closing
    setTimeout(() => {
      setFormData({
        full_name: "",
        email: "",
        phone_number: "",
        company_name: "",
        type: "",
        source: "Quick Contact Form",
      });
      setSubmitStatus("");
      setIsSubmitting(false);
    }, 300);
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      scrollable
      backdrop="static"
      className="strategy-modal"
    >
      <div className="modal-content p-3">
        <div className="modal-header">
          <h5 className="modal-title">Book a Free Strategy Call</h5>
          <Button className="btn-close" onClick={handleClose}></Button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Full Name*</label>
              <input
                type="text"
                className="form-control"
                id="full_name"
                placeholder="Full Name"
                value={formData.full_name}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Business Email*</label>
              <input
                type="email"
                className="form-control"
                id="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Phone Number*</label>
              <input
                type="tel"
                className="form-control"
                id="phone_number"
                placeholder="Phone Number"
                value={formData.phone_number}
                onChange={handlePhoneChange}
                maxLength="10"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Company Name</label>
              <input
                type="text"
                className="form-control"
                id="company_name"
                placeholder="Company(optional)"
                value={formData.company_name}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>

            <div className="mb-4">
              <label className="form-label">Business Type*</label>
              <select
                className="form-select"
                id="type"
                value={formData.type}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              >
                <option value="" disabled>
                  Select Business Type
                </option>
                <option>B2B</option>
                <option>B2C</option>
                <option>B2B2C</option>
                <option>OTHERS</option>
              </select>
            </div>

            {submitStatus && (
              <div className="alert alert-danger">{submitStatus}</div>
            )}

            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Sending...
                </>
              ) : (
                "Schedule My Free Call"
              )}
            </button>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default ContactModel;
